/**
 * The routing root.
 *
 * Order matters and is deliberate: an error boundary outside everything (so a
 * render crash is a readable card rather than a white screen), then the invite
 * route which must work while signed out, then the session gate, then the
 * tenant gate, then the role split into one of the two shells.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { AlertTriangle } from 'lucide-react';
import { useSession } from './lib/session.tsx';
import { TipcardHost } from './components/Tipcard.tsx';
import { PandaMark, Spinner } from './components/Bits.tsx';
import LoginPage from './pages/LoginPage.tsx';
import AcceptInvitePage from './pages/AcceptInvitePage.tsx';
import ResetPasswordPage from './pages/ResetPasswordPage.tsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.tsx';
import TenantPickPage from './pages/TenantPickPage.tsx';
import BossShell from './components/BossShell.tsx';
import StaffShell from './components/StaffShell.tsx';

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Left as console output on purpose: there is no error reporting service
    // wired up yet, and swallowing it silently would make a crash unreproducible.
    console.error('Unhandled UI error', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="card card-lift max-w-md p-6 text-center">
          <span
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center"
            style={{
              borderRadius: 'var(--sp-radius-pill)',
              background: 'var(--sp-warn-soft)',
              color: 'var(--sp-warn-strong)',
            }}
          >
            <AlertTriangle size={22} aria-hidden="true" />
          </span>
          <h1 className="text-[17px] font-bold text-ink">Sneaky Panda hit a snag</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            Something on this screen failed to draw. Reloading usually clears it, and nothing you had saved is
            affected.
          </p>
          <button type="button" className="btn btn-primary mt-5" onClick={() => window.location.reload()}>
            Reload the app
          </button>
        </div>
      </div>
    );
  }
}

function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <PandaMark size={56} />
      <Spinner label="Signing you in" />
    </div>
  );
}

function Gate() {
  const { me, loading, memberships, tenant, isBoss } = useSession();
  const [location] = useLocation();

  // Invite and reset links must render for a signed-out person, and /forgot
  // is reachable straight off the login screen.
  if (location.startsWith('/invite') || location.startsWith('/reset') || location.startsWith('/forgot')) {
    return (
      <Switch>
        <Route path="/invite/:token" component={AcceptInvitePage} />
        <Route path="/reset/:token" component={ResetPasswordPage} />
        <Route path="/forgot" component={ForgotPasswordPage} />
        <Route component={AcceptInvitePage} />
      </Switch>
    );
  }

  if (loading) return <Splash />;
  if (!me) return <LoginPage />;
  if (memberships.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="card card-lift max-w-md p-6 text-center">
          <h1 className="text-[17px] font-bold text-ink">No workplace yet</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
            Your account is not attached to a business. Ask your manager to send you an invite link.
          </p>
        </div>
      </div>
    );
  }
  if (!tenant) return <TenantPickPage />;

  return isBoss ? <BossShell /> : <StaffShell />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <TipcardHost />
      <Gate />
    </ErrorBoundary>
  );
}
